import { useRefetchContext } from 'components/SpaceBooking/SpaceTable/RefetchContext';
import { MappedSpace } from 'components/SpaceBooking/SpaceTable/types';
import { useModal } from 'hooks/useModal';
import moment, { Moment } from 'moment';
import React, { CSSProperties, useCallback, useMemo } from 'react';
import BaseTable, { ColumnShape } from 'react-base-table';
import { BlockingCell } from '.';
import { SpaceBlockingForm, SpaceBookingAssistent } from '..';
import { useClubSlug } from '../../Clubs/hooks';
import { SpaceGroup } from '../types';

interface SpacePlaceholderProps {
    isClosed: boolean;
    vertical?: boolean;
}

const SpacePlaceholder: React.FC<React.PropsWithChildren<React.PropsWithChildren<SpacePlaceholderProps>>> = ({ isClosed, vertical }) => {
    if (isClosed) return null;

    return (
        <div
            className="tw-absolute tw-bottom-1/2 tw-right-1/2 tw-translate-x-1/2 tw-translate-y-1/2 tw-text-[0.45rem] tw-text-[rgb(200,200,200)]"
            style={{
                writingMode: vertical ? 'horizontal-tb' : 'vertical-lr'
            }}
        >
            FREI
        </div>
    );
};

interface StyledSpaceCellProps {
    isClosed: boolean;
    style?: CSSProperties;
    onClick: () => void;
    children?: React.ReactNode;
}

const StyledSpaceCell: React.FC<React.PropsWithChildren<React.PropsWithChildren<StyledSpaceCellProps>>> = ({ isClosed, style, onClick, children }) => {
    return (
        <div
            onClick={onClick}
            style={style}
            className={[
                'tw-relative tw-h-full tw-w-full tw-transition-[border] tw-duration-200',
                !isClosed ? 'tw-cursor-pointer hover:tw-bg-[rgb(228,228,228)]' : 'tw-bg-[rgb(228,228,228)]'
            ].join(' ')}
        >
            {children}
        </div>
    );
};

export interface SpaceCellProps {
    expandedRowKeys: string[];
    selectedDate: Moment;
    spaceGroup: SpaceGroup;
    isScrolling?: boolean;
    cellData: any;
    columns: ColumnShape<MappedSpace>[];
    column: ColumnShape<MappedSpace>;
    columnIndex: number;
    rowData: any;
    rowIndex: number;
    container: BaseTable<undefined>;
    isOwner?: boolean;
    clubSlug?: string;
}

export const SpaceCell = ({
    cellData,
    column,
    rowData,
    selectedDate,
    spaceGroup,
    columnIndex,
    isOwner = false,
    clubSlug: _clubSlug
}: SpaceCellProps) => {
    const startTime = columnIndex;
    const startDate = rowData.date;
    const space = rowData;

    const clubSlug = useClubSlug(_clubSlug);
    const currentDate = moment();
    const [setModal, resetModal] = useModal();
    const currentTimeIndex = moment().hours() * 4 + Math.floor(moment().minutes() / 15);
    const { refetchAll } = useRefetchContext();

    const isCurrentTime = useMemo(
        () => startTime === currentTimeIndex && moment().isSame(startDate, 'day'),
        [currentTimeIndex, startTime, startDate, selectedDate]
    );

    const isClosed = useMemo(() => {
        const openingHours = rowData.opening_hours;
        return (
            (startDate?.isBefore(currentDate) && !startDate?.isSame(currentDate, 'day')) ||
            (startDate?.isBefore(currentDate) && startTime <= currentTimeIndex) ||
            (openingHours?.start !== null && openingHours?.start > startTime) ||
            (openingHours?.end !== null && openingHours?.end <= startTime)
        );
    }, [column, currentDate, startDate, startTime, rowData, currentTimeIndex]);

    const blocking = useMemo(
        () => rowData.blockings?.find((blocking: any) => blocking.start_time === columnIndex),
        [rowData, columnIndex]
    );

    const handleCreateBlocking = useCallback(() => {
        if ((isOwner || !isClosed) && !blocking) {
            setModal({
                title: 'Sperrung erstellen',
                content: (
                    <SpaceBlockingForm
                        space={space}
                        startDate={startDate}
                        startTime={startTime}
                        clubSlug={clubSlug}
                        callback={() => {
                            resetModal();
                            refetchAll();
                        }}
                    />
                ),
                show: true
            });
        }
    }, [isOwner, isClosed, blocking, space, startDate, startTime, clubSlug, resetModal, refetchAll, setModal]);

    const handleClick = useCallback(() => {
        if ((isOwner || !isClosed) && !blocking) {
            setModal({
                title: 'Platz buchen',
                content: (
                    <SpaceBookingAssistent
                        space={space}
                        clubSlug={clubSlug}
                        spaceGroup={spaceGroup}
                        startDate={startDate}
                        startTime={startTime}
                    />
                ),
                show: true
            });
        }
    }, [isOwner, isClosed, blocking, space, startDate, startTime, spaceGroup, clubSlug, setModal]);

    const spaceCellStyle = useMemo<CSSProperties>(
        () => ({
            borderRightWidth: isCurrentTime ? 3 : 1,
            borderRightStyle: 'solid',
            borderColor: isCurrentTime ? '#38c172' : columnIndex % 4 === 3 ? 'rgb(200, 200, 200)' : 'rgb(238, 238, 238)'
        }),
        [isCurrentTime, columnIndex]
    );

    if (typeof cellData !== 'undefined') return cellData;

    return (
        <StyledSpaceCell
            isClosed={isClosed}
            style={spaceCellStyle}
            onClick={() => (!isOwner ? handleClick() : handleCreateBlocking())}
        >
            {blocking && <BlockingCell isOwner={isOwner} blocking={blocking} />}
            {!isOwner && <SpacePlaceholder isClosed={isClosed} />}
        </StyledSpaceCell>
    );
};
